import React from 'react';
import { motion } from 'framer-motion';
import { 
  CodeBracketIcon, 
  ArrowTopRightOnSquareIcon,
  CpuChipIcon,
  DocumentMagnifyingGlassIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const Projects: React.FC = () => {
  const projects: {
    title: string;
    description: string;
    technologies: string[];
    icon: React.ReactNode;
    color: string;
    image: string | null;
    github: string | null;
    demo: string | null;
    company: string;
    logo: string | null;
    highlights: string[];
  }[] = [
    {
      title: "mo3da.com: Heavy Equipment Marketplace",
      description: "Took Loxport's marketplace for heavy transport equipment in Egypt from first commit to App Store submission in less than six months, across website, iOS, Android and a full back office. Arabic-first, with a structured spec sheet and search filters for each equipment category, staff moderation of every listing, seller verification, in-app chat, WhatsApp alerts for saved searches and an Egyptian-Arabic AI assistant. Owned vision, roadmap, scope, delivery, release and success measurement for the cross-functional Scrum team, from structuring new use cases with CIRCLES and prioritising with RICE to user stories with testable acceptance criteria to hands-on acceptance testing as an ISTQB-certified tester.",
      technologies: ["CIRCLES", "RICE", "React", "React Native", "Node.js", "PostgreSQL", "Cloudflare R2", "WhatsApp API", "Scrum", "Jira", "Confluence", "Postman", "Cypress", "Maestro", "ISTQB"],
      icon: <SparklesIcon className="h-8 w-8" />,
      color: "from-amber-400 to-yellow-600",
      image: "/projects/mo3da app.jpg",
      github: null,
      demo: "https://mo3da.com",
      company: "Loxport",
      logo: "/companies/loxport.jpg",
      highlights: [
        "0 to launch in less than 6 months on web, iOS and Android",
        "< 2% post-release defect rate",
        "95%+ sprint goal achievement"
      ]
    },
    {
      title: "Fleety: Fleet Intelligence Platform",
      description: "Built Loxport's in-house fleet telematics platform for heavy-haul and freight trucks, turning on-road data into control-room decisions: live tracking from CAN-bus data with geofencing and route-deviation alerts, axle-weight monitoring, driver scorecards and hours-of-service compliance, fuel analytics and predictive maintenance from engine fault codes.",
      technologies: ["Telematics", "J1939 / CAN-bus", "Predictive Maintenance", "Geofencing", "Fleet Analytics"],
      icon: <CpuChipIcon className="h-8 w-8" />,
      color: "from-green-500 to-blue-600",
      image: "/projects/loxport fleety.jpg",
      github: null,
      demo: null,
      company: "Loxport",
      logo: "/companies/loxport.jpg",
      highlights: [
        "Target: 8–12% less idle time and fuel waste",
        "Target: 15% less unscheduled downtime",
        "~20 min faster dispatch per load"
      ]
    },
    {
      title: "Data Collect: Logistics Site Data & Documents",
      description: "Built an internal operations hub that replaced fragmented reporting across Loxport's logistics sites, including Cairo, Alexandria and Port Said. It enforces structured document uploads, tracks completion per site, maps each site to an accountable project manager and gives management live oversight with one-click update requests.",
      technologies: ["Internal Tools", "Document Workflows", "Dashboards", "Operations"],
      icon: <DocumentMagnifyingGlassIcon className="h-8 w-8" />,
      color: "from-blue-600 to-indigo-800",
      image: "/projects/loxport data collect.jpg",
      github: null,
      demo: null,
      company: "Loxport",
      logo: "/companies/loxport.jpg",
      highlights: [
        "80% faster document retrieval",
        "Fewer missing compliance files across sites",
        "Live visibility for management on every site"
      ]
    },
    {
      title: "AI Extraction & Matching in Production",
      description: "Integrated LLM-based extraction, matching and document generation into existing workflows. Unstructured input is normalised, matched with confidence scoring, and anything below the threshold is routed to human review, keeping results reliable on messy real-world data.",
      technologies: ["LLMs", "Confidence Scoring", "Human-in-the-Loop", "Monitoring", "Prompt Engineering"],
      icon: <SparklesIcon className="h-8 w-8" />,
      color: "from-purple-600 to-pink-700",
      image: null,
      github: null,
      demo: null,
      company: "Loxport",
      logo: "/companies/loxport.jpg",
      highlights: [
        "Confidence thresholds with human review below the bar",
        "Normalisation of unstructured input data",
        "Monitored in production for accuracy"
      ]
    },
    {
      title: "Automated Data & Operations Pipelines",
      description: "Replaced manual operational steps with n8n and Python pipelines built with schema validation, retries, error handling and monitoring, because a pipeline that fails silently is worse than the manual process it replaces.",
      technologies: ["n8n", "Python", "Schema Validation", "REST APIs", "Webhooks"],
      icon: <CpuChipIcon className="h-8 w-8" />,
      color: "from-emerald-500 to-teal-600",
      image: null,
      github: null,
      demo: null,
      company: "Loxport",
      logo: "/companies/loxport.jpg",
      highlights: [
        "Manual steps replaced with monitored automation",
        "Retries and error handling built in",
        "Integrations defined together with engineering"
      ]
    },
    {
      title: "Evidence-Based Prioritisation with SQL",
      description: "Ran my own SQL analyses on the production database to find where processes break, quantify what they cost and base roadmap decisions on evidence, combined with discovery directly with operational users.",
      technologies: ["SQL", "Data Analysis", "Discovery", "Roadmapping"],
      icon: <DocumentMagnifyingGlassIcon className="h-8 w-8" />,
      color: "from-cyan-500 to-blue-600",
      image: null,
      github: null,
      demo: null,
      company: "Loxport",
      logo: "/companies/loxport.jpg",
      highlights: [
        "Weak points found and sized in the data",
        "20+ competing topics prioritised",
        "Documented decisions on what not to build"
      ]
    },
    {
      title: "Audit-Ready Project Delivery",
      description: "Led several parallel construction and infrastructure projects end to end within a regulated framework, where documentation, approvals and evidence had to withstand external audits, and owned the commercial chain from cost estimate to invoice.",
      technologies: ["Project Management", "Budget Control", "Compliance", "Stakeholder Management"],
      icon: <CodeBracketIcon className="h-8 w-8" />,
      color: "from-orange-500 to-red-600",
      image: null,
      github: null,
      demo: null,
      company: "B+P GmbH",
      logo: "/companies/bp-group.jpg",
      highlights: [
        "Parallel projects: scope, budget, resources, schedule",
        "Purchase order, delivery and invoice matching",
        "Clients, contractors and authorities aligned"
      ]
    }
  ];



  return (
    <section id="projects" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Selected Projects
          </h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto rounded-full"></div>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Products, pipelines and projects I've owned, from data and AI in production to regulated delivery
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.01 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100"
            >
              {/* Project Image */}
              {project.image && (
                <div className="relative h-64 bg-gray-50 flex items-center justify-center overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={`${project.title} screenshot`}
                    className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  {/* Icon overlay */}
                  {/* <div className="absolute top-4 left-4">
                    <div className={`p-3 rounded-xl bg-white/90 backdrop-blur-sm shadow-lg`}>
                      <div className={`text-transparent bg-gradient-to-r ${project.color} bg-clip-text`}>
                        {project.icon}
                      </div>
                    </div>
                  </div> */}
                </div>
              )}
              
              <div className="p-8">
                {/* Title and Links */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors duration-300 mb-2">
                      {project.title}
                    </h3>
                    {project.company && (
                      <div className="flex items-center">
                        {project.logo && (
                          <img
                            src={project.logo}
                            alt={`${project.company} logo`}
                            className="w-4 h-4 object-contain mr-2"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        )}
                        <span className="text-sm text-primary-600 font-medium">
                          {project.company}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {project.github && (
                      <motion.a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors bg-gray-50 rounded-lg"
                      >
                        <CodeBracketIcon className="h-5 w-5" />
                      </motion.a>
                    )}
                    {project.demo && (
                      <motion.a
                        href={project.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors bg-gray-50 rounded-lg"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      </motion.a>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
                  {project.description}
                </p>

                {/* Highlights */}
                <div className="mb-6">
                  <div className="grid grid-cols-1 gap-2">
                    {project.highlights.slice(0, 3).map((highlight, highlightIndex) => (
                      <div key={highlightIndex} className="flex items-center text-sm">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${project.color} mr-3 flex-shrink-0`}></div>
                        <span className="text-gray-700 font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.slice(0, 4).map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className={`px-3 py-1 bg-gradient-to-r ${project.color} text-white text-xs font-medium rounded-full border border-gray-200 hover:bg-opacity-90 transition-all duration-200`}
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 4 && (
                      <span className="px-3 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                        +{project.technologies.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Projects CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-8 rounded-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Want the Full Picture?
            </h3>
            <p className="text-gray-600 mb-6">
              Download my resume for the complete overview of my experience, certifications and skills.
            </p>
            <motion.a
              href="/cv/Ahmed_Youssef_CV.pdf"
              download="Ahmed_Youssef_CV.pdf"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-medium rounded-lg transition-colors shadow-lg hover:shadow-xl"
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
              Download Resume
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;